import {
  TracingAreaRoutes,
  VEHICLE_TRACING_RIGHTS,
} from '@backoffice-frontend/tracing-api'
import type { BoumRight } from '@moia-dev/moia-token-claims'

export type MinervaNavigationArea = 'tracing'

export type MinervaNavigation = {
  area: MinervaNavigationArea
  link: string
}

type MinervaNavigationTarget = {
  /** The only path a link may open; the model's link is never trusted beyond it. */
  route: string
  allowedRights: BoumRight[]
}

// Only vehicle tracing for now. Adding an area here is all a new chip needs,
// apart from its label in MinervaNavigationButton.
const MINERVA_NAVIGATION_TARGETS: Record<
  MinervaNavigationArea,
  MinervaNavigationTarget
> = {
  tracing: {
    route: TracingAreaRoutes.Vehicle.route,
    allowedRights: VEHICLE_TRACING_RIGHTS,
  },
}

const isMinervaNavigationArea = (area: string): area is MinervaNavigationArea =>
  Object.hasOwn(MINERVA_NAVIGATION_TARGETS, area)

/**
 * The navigation item of an answer, or undefined when the model proposed none
 * or one the chat cannot open: an unknown area, or a link to any path other
 * than the area's route (another area, another origin, a `//host` path).
 */
export const parseMinervaNavigation = (
  item: { area: string; link: string } | null | undefined,
): MinervaNavigation | undefined => {
  if (!item) return undefined
  const area = item.area.trim().toLowerCase()
  if (!isMinervaNavigationArea(area)) return undefined
  const { route } = MINERVA_NAVIGATION_TARGETS[area]
  const trimmed = item.link.trim()
  // Without the leading slash the router would resolve it from the dashboard.
  const link = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  if (link.split(/[?#]/, 1)[0] !== route) return undefined
  return { area, link }
}

export const getMinervaNavigationRights = (
  area: MinervaNavigationArea,
): BoumRight[] => MINERVA_NAVIGATION_TARGETS[area].allowedRights
